import {JoyrideStepService} from 'ngx-joyride';

/**
 * Correctifs de scroll de ngx-joyride (v2.5), appliqués sur les prototypes de la lib.
 *
 * La lib enchaîne jusqu'à quatre scrolls concurrents par étape (scrollIntoView maison,
 * scrollIntoView NATIF qui aligne la cible en haut de fenêtre — donc sous le header
 * fixe —, puis deux ajustements basés sur un winTopPosition mis à jour de façon
 * asynchrone, donc périmé). Résultat : zone entourée sous le header, bulles coupées.
 *
 * Remplacement par UN placement pré-affichage + UN ajustement post-affichage :
 * - pré-affichage (scrollIfElementBeyondOtherElements) : cible posée sous le header
 *   avec l'espace d'une bulle au-dessus (ancre adaptée à la hauteur de la cible),
 *   dans son conteneur scrollable (bandeau de filtres…) ou dans la fenêtre ;
 * - post-affichage (scrollIfStepAndTargetAreNotVisible) : recadrage fenêtre d'après
 *   les rects RÉELS de la bulle et de la zone entourée — jamais pour une cible en
 *   conteneur (scroller la fenêtre désalignerait bulle/backdrop, absolus document).
 * - étapes 'center' et cibles fixes : aucun scroll.
 * Tous les scrolls sont instantanés (un recadrage différé/smooth = à-coups visibles).
 */
export function patchJoyrideScroll() {
    const proto = JoyrideStepService.prototype as any;
    if (proto.__lyboxScrollFix) {
        return;
    }
    proto.__lyboxScrollFix = true;

    // Bas du header fixe (bandeaux promo/essai inclus quand ils sont dans le nav)
    const headerBottom = () => {
        const nav = document.querySelector('nav.navbar');
        return nav ? Math.max(0, Math.round(nav.getBoundingClientRect().bottom)) : 0;
    };

    const isBody = (el: any) =>
        !el || el === document.body || el === document.documentElement;

    // Premier ancêtre qui scrolle RÉELLEMENT. getFirstScrollableParent de la lib
    // s'arrête au premier overflow auto/scroll même sans débordement (ex. les
    // .card-body des widgets) : le scroll de page n'opérait alors jamais.
    const realScrollParent = (el: HTMLElement) => {
        let node = el.parentElement;
        while (node && node !== document.body) {
            const cs = getComputedStyle(node);
            if (/(auto|scroll|overlay)/.test(cs.overflow + cs.overflowY)
                && node.scrollHeight > node.clientHeight + 4) {
                return node;
            }
            node = node.parentElement;
        }
        return document.body;
    };

    const skipStep = (step: any) =>
        !step || step.position === 'center';

    // Placement unique avant l'affichage de la bulle
    proto.scrollIfElementBeyondOtherElements = function() {
        const step = this.currentStep;
        if (skipStep(step)) {
            return;
        }
        const el = step.targetViewContainer.element.nativeElement as HTMLElement;
        const rect = el.getBoundingClientRect();
        // Cible haute : moins d'espace réservé à la bulle, sinon la cible déborde en bas
        const ancre = headerBottom() + (rect.height > 300 ? 250 : 340);
        const parent = realScrollParent(el);
        if (!isBody(parent)) {
            // Conteneur scrollable (bandeau de filtres fixed compris : scroller le
            // conteneur déplace bien la cible à l'écran)
            parent.scrollTop += rect.top - ancre;
        } else if (!step.isElementOrAncestorFixed) {
            window.scrollBy(0, rect.top - ancre);
        }
    };

    proto.scrollIfStepAndTargetAreNotVisible = function() {
        const step = this.currentStep;
        if (skipStep(step) || step.isElementOrAncestorFixed) {
            return;
        }
        const el = step.targetViewContainer.element.nativeElement as HTMLElement;
        if (!isBody(realScrollParent(el))) {
            return;
        }
        // Recadrage fenêtre : UN delta (les rects seraient périmés après un premier
        // scroll), le « haut visible » commençant sous le header — prioritaire si tout
        // ne tient pas. Ne scrolle que si nécessaire.
        const recadrer = () => {
            if (this.currentStep !== step) {
                return;
            }
            const rects = ['.joyride-step__container', '.backdrop-target']
                .map(s => document.querySelector(s))
                .filter(e => !!e)
                .map(e => e.getBoundingClientRect())
                .filter(r => r.height > 0);
            if (!rects.length) {
                return;
            }
            const maxBottom = Math.max(...rects.map(r => r.bottom));
            const minTop = Math.min(...rects.map(r => r.top));
            const limit = headerBottom() + 8;
            let delta = 0;
            if (maxBottom > window.innerHeight - 8) {
                delta = maxBottom - (window.innerHeight - 8);
            }
            if (minTop - delta < limit) {
                delta = minTop - limit;
            }
            if (Math.abs(delta) > 2) {
                window.scrollBy(0, delta);
            }
        };
        // La bulle n'est positionnée qu'à son ngAfterViewInit : mesurer tout de suite
        // donne un rect fantôme -> rAF (avant le paint, aucun saut visible). Les deux
        // re-vérifications rattrapent un scroll tardif de la page (contenu qui charge) ;
        // elles ne scrollent que si la bulle ou la zone sortent réellement des limites.
        requestAnimationFrame(recadrer);
        setTimeout(recadrer, 300);
        setTimeout(recadrer, 800);
        setTimeout(recadrer, 1500);
    };
}
